import { useState, useCallback, useRef } from 'react';
import type { MediaItem } from '@/types';
import { validateImage, validateVideo } from '@/lib/media-validator';
import VideoPreview from './VideoPreview';

interface MediaUploaderProps {
  images: MediaItem[];
  video?: MediaItem;
  onImagesChange: (images: MediaItem[]) => void;
  onVideoChange: (video?: MediaItem) => void;
}

export default function MediaUploader({ images, video, onImagesChange, onVideoChange }: MediaUploaderProps) {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    async (files: FileList) => {
      setUploading(true);
      const newImages: MediaItem[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file.type.startsWith('image/')) continue;

        const validation = validateImage(file);
        if (!validation.valid) {
          alert(validation.errors.join('\n'));
          continue;
        }

        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          newImages.push({
            id: crypto.randomUUID(),
            key: file.name,
            url,
            type: 'image',
            mimeType: file.type,
            size: file.size,
            width: img.naturalWidth,
            height: img.naturalHeight,
            uploadedAt: new Date().toISOString(),
          });

          if (newImages.length === files.length || i === files.length - 1) {
            onImagesChange([...images, ...newImages]);
            setUploading(false);
          }
        };
        img.src = url;
      }
    },
    [images, onImagesChange],
  );

  const handleVideoFile = useCallback(
    async (file: File) => {
      const validation = validateVideo(file);
      if (!validation.valid) {
        alert(validation.errors.join('\n'));
        return;
      }

      const url = URL.createObjectURL(file);
      onVideoChange({
        id: crypto.randomUUID(),
        key: file.name,
        url,
        type: 'video',
        mimeType: file.type,
        size: file.size,
        uploadedAt: new Date().toISOString(),
      });
    },
    [onVideoChange],
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files?.length) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles],
  );

  const removeImage = useCallback(
    (id: string) => {
      onImagesChange(images.filter((img) => img.id !== id));
    },
    [images, onImagesChange],
  );

  const moveImage = useCallback(
    (fromIndex: number, toIndex: number) => {
      const updated = [...images];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      onImagesChange(updated);
    },
    [images, onImagesChange],
  );

  const removeVideo = useCallback(() => {
    onVideoChange(undefined);
  }, [onVideoChange]);

  return (
    <div className="space-y-6">
      <div>
        <label className="label-field mb-2 block">Product Images</label>
        <div
          className={`relative rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
            dragActive ? 'border-primary-600 bg-primary-50' : 'border-border'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={(e) => e.target.files && handleFiles(e.target.files)}
          />
          <svg className="mx-auto h-12 w-12 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="mt-2 text-sm text-muted-foreground">
            Drag & drop images here, or{' '}
            <button type="button" onClick={() => inputRef.current?.click()} className="text-primary-600 hover:underline">
              browse files
            </button>
          </p>
          <p className="mt-1 text-2xs text-muted-foreground">JPG, PNG, WebP (max 5MB each)</p>
        </div>
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-4 gap-4 sm:grid-cols-6">
          {images.map((img, index) => (
            <div key={img.id} className="group relative aspect-square overflow-hidden rounded-lg border border-border">
              <img src={img.url} alt="" className="h-full w-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                <div className="flex gap-1">
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => moveImage(index, index - 1)}
                      className="rounded bg-white/90 p-1 text-foreground hover:bg-white"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  )}
                  {index < images.length - 1 && (
                    <button
                      type="button"
                      onClick={() => moveImage(index, index + 1)}
                      className="rounded bg-white/90 p-1 text-foreground hover:bg-white"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeImage(img.id)}
                    className="rounded bg-destructive/90 p-1 text-white hover:bg-destructive"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              {index === 0 && (
                <span className="absolute left-1 top-1 rounded bg-primary-600 px-1.5 py-0.5 text-2xs font-medium text-white">
                  Main
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="pt-4">
        <label className="label-field mb-2 block">Product Video</label>
        {video ? (
          <div className="space-y-3">
            <VideoPreview media={video} />
            <button type="button" onClick={removeVideo} className="text-sm text-destructive hover:underline">
              Remove video
            </button>
          </div>
        ) : (
          <div>
            <input
              ref={videoInputRef}
              type="file"
              accept="video/mp4,video/webm"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleVideoFile(e.target.files[0])}
            />
            <button
              type="button"
              onClick={() => videoInputRef.current?.click()}
              className="btn-secondary"
            >
              <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Upload Video
            </button>
            <p className="mt-1 text-2xs text-muted-foreground">MP4, WebM (max 100MB)</p>
          </div>
        )}
      </div>

      {uploading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Processing images...
        </div>
      )}
    </div>
  );
}
